import { useState, useEffect, useMemo } from "react";
import { BsBuildingCheck, BsGeoAlt, BsExclamationTriangle } from "react-icons/bs";
import { useAuthContext } from "../../context/auth";
import { getInventarioInfra, construirInventarioInfra, TIPO_PANTALLA_LABEL, ESTADO_PANTALLA_LABEL } from "../../utils";
import { Loader } from "../Loader";
import styles from "./styles.module.scss";

// Vista de SOLO LECTURA del inventario de pantallas (sección Infraestructura).
// Jerarquía: bloque → edificios → pantallas. Sin edición, sin acciones.

const badgeEstadoClass = (estado) => {
  if (estado === "operativa") return styles.badgeOperativa;
  if (estado === "precisa_mantenimiento") return styles.badgePrecisa;
  return styles.badgeNeutro;
};

const PantallasTabla = ({ pantallas }) => {
  if (pantallas.length === 0) {
    return <div className={styles.edificioVacio}>Sin pantallas cargadas.</div>;
  }
  return (
    <div className={styles.tableResponsive}>
      <table className={styles.tablaPantallas}>
        <thead>
          <tr>
            <th>Etiqueta</th>
            <th>Tipo</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {pantallas.map((pan) => (
            <tr key={pan.id}>
              <td>{pan.etiqueta || "—"}</td>
              <td>{TIPO_PANTALLA_LABEL[pan.tipo] || pan.tipo}</td>
              <td>
                <span className={`${styles.badge} ${badgeEstadoClass(pan.estado)}`}>
                  {ESTADO_PANTALLA_LABEL[pan.estado] || pan.estado}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EdificioBloque = ({ edificio }) => (
  <div className={styles.edificio}>
    <div className={styles.edificioHeader}>
      <span className={styles.edificioNombre}>{edificio.edificio}</span>
      {edificio.ubicacion ? (
        <span className={styles.edificioComuna}>
          <BsGeoAlt /> {edificio.ubicacion}
        </span>
      ) : null}
      <span className={styles.edificioCount}>{edificio.totalPantallas} pantallas</span>
    </div>
    <PantallasTabla pantallas={edificio.pantallas} />
  </div>
);

const BloqueSection = ({ nombre, esSatelite, totalEdificios, totalPantallas, edificios }) => (
  <section className={styles.bloque}>
    <header className={styles.bloqueHeader}>
      <h2 className={styles.bloqueNombre}>
        {nombre}
        {esSatelite ? <span className={styles.satelite}>satélite · quincenal</span> : null}
      </h2>
      <div className={styles.bloqueTotales}>
        <span>{totalEdificios} edificios</span>
        <span className={styles.dot}>·</span>
        <span>{totalPantallas} pantallas</span>
      </div>
    </header>
    {edificios.length > 0 ? (
      edificios.map((edi) => <EdificioBloque key={edi.id} edificio={edi} />)
    ) : (
      <div className={styles.edificioVacio}>Sin edificios asignados.</div>
    )}
  </section>
);

const InfraInventarioPantallas = () => {
  const { pais } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState({ bloques: [], edificios: [], pantallas: [], errores: [] });

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    getInventarioInfra(pais)
      .then((data) => {
        if (vivo) setRaw(data);
      })
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
  }, [pais]);

  const inv = useMemo(() => construirInventarioInfra(raw), [raw]);
  const vacio = inv.totales.pantallas === 0 && inv.totales.bloques === 0;

  if (loading) return <Loader />;

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            <BsBuildingCheck /> Inventario de Pantallas
          </h1>
          <p className={styles.pageSubtitle}>Vista de solo lectura — bloques, edificios y pantallas.</p>
        </div>
        <div className={styles.totalesGlobales}>
          <div className={styles.totalCard}>
            <span className={styles.totalNum}>{inv.totales.bloques}</span>
            <span className={styles.totalLabel}>Bloques</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalNum}>{inv.totales.edificios}</span>
            <span className={styles.totalLabel}>Edificios</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalNum}>{inv.totales.pantallas}</span>
            <span className={styles.totalLabel}>Pantallas</span>
          </div>
        </div>
      </header>

      {vacio ? (
        <div className={styles.emptyState}>
          <BsExclamationTriangle className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>Sin datos de inventario</p>
          <p className={styles.emptyText}>
            {raw.errores?.length
              ? "Las tablas del inventario todavía no están disponibles. Se poblarán al aplicar las migraciones."
              : "No hay pantallas cargadas para este país."}
          </p>
        </div>
      ) : (
        <>
          {inv.gruposBloque.map((g) => (
            <BloqueSection
              key={g.bloque.id}
              nombre={g.bloque.nombre}
              esSatelite={g.bloque.es_satelite}
              totalEdificios={g.totalEdificios}
              totalPantallas={g.totalPantallas}
              edificios={g.edificios}
            />
          ))}
          {inv.sinBloque.totalEdificios > 0 ? (
            <BloqueSection
              nombre="Sin bloque asignado"
              esSatelite={false}
              totalEdificios={inv.sinBloque.totalEdificios}
              totalPantallas={inv.sinBloque.totalPantallas}
              edificios={inv.sinBloque.edificios}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default InfraInventarioPantallas;
